import { Controller } from "stimulus"

/**
 * Stimulus Controller on steroids. Used to get other controllers. A controller identifier is what is written into
 * data-controller in the DOM. This identifier, lets says foo-bar, is used to store a reference to the controller
 * in connect (1), this every DOM node with a controller stores a reference to the controller using its identifier,
 * e.g. domNode['foo-bar'] is a reference to the controller on a DOM node. 
 * 
 * Let's summarize:
 * 
 * 1. store a controller reference in the controllers DOM node in `connect`
 * 2. query DOM nodes by using the `const el = document.querySelector('[data-controller="foo-bar"]')`
 * 3. get the controller `const controller = el['foo-bar']`
 * 
 * This class adds some methods that helps to retrieve one, all, sub-controllers or sibling-controllers.
 * 
 * @class
 */
class AdvancedController extends Controller {

  _controllersCache = []

  connect() {
    this.element[this.identifier] = this // (1)
  }

  /**
   * Retrieve a single controller, e.g. look up the search controller in the navigation controller
   * @example
   *  // in the navigation controller
   *  onClick() {
   *    // ...
   *    this.getController('search').close()
   *  }
   * @param {string} identifier The identifier written in data-controller="this-is-the-identifier"
   * @returns {AdvancedController} The target controller
   */
  getController(identifier) {
    if (identifier in this._controllersCache) return this._controllersCache[identifier];
    const otherController = document.querySelector(`[data-controller="${identifier}"]`)[`${identifier}`]
    this._controllersCache[identifier] = otherController
    return otherController
  }

  /**
   * Retrieve all controllers for a given identifier. It is not important, where they are in the DOM, as oposed to
   * `getSubControllers`, that looks only on the DOM subtree of the current controller.
   * @param {string} identifier The identifier of the controller
   * @returns {Array} All found controllers
   */
  getAllControllers(identifier) {
    const allControllerNodes = document.querySelectorAll(`[data-controller="${identifier}"]`)

    const allControllers = [];
    for (const someControllerNode of allControllerNodes) {
      const someController = someControllerNode[`${identifier}`];
      allControllers.push(someController)
    }

    return allControllers
  }

  /**
   * Retrieves all controllers on the DOM subtree (i.e. below).
   * @example
   *  // in an accordion-controller
   *  const itemControllers = this.getSubControllers('accordion-item')
   *  // iterate and call a message on each sub-controller
   * @param {string} identifier The identifier of the sub-controllers
   * @returns {Array} All found sub-controllers
   */
  getSubControllers(identifier) {
    const allControllerNodes = this.element.querySelectorAll(`[data-controller="${identifier}"]`)

    const otherControllers = [];
    for (const otherControllerNode of allControllerNodes) {
      const otherController = otherControllerNode[`${identifier}`];
      if (otherController !== this) {
        otherControllers.push(otherController)
      }
    }

    return otherControllers
  }

  /**
   * Look controllers up on siblings, i.e. on the same DOM level. Excludes ourselves.
   * @example
   *  // inside an accordion-item controller
   *  onClick() {
   *    const siblings = this.getSiblingControllers('accordion-item');
   *    // iterate siblings and call close() method on each
   *  }
   * @param {string} identifier 
   * @returns {Array} All found sibling controllers (without the current controller)
   */
  getSiblingControllers(identifier) {
    const allControllerNodes = this.element.parentElement.querySelectorAll(`[data-controller="${identifier}"]`)

    const otherControllers = [];
    for (const otherControllerNode of allControllerNodes) {
      const otherController = otherControllerNode[`${identifier}`]
      if (otherController !== this && otherControllerNode.parentElement === this.element.parentElement) {
        otherControllers.push(otherController)
      }
    }

    return otherControllers
  }

}

export default AdvancedController;